(() => {
  if (window.__ts2TestimonialsLoaded) return;
  window.__ts2TestimonialsLoaded = true;

  const SECTION_SELECTOR = ".ts2";
  const instances = new WeakMap();

  function debounce(fn, wait) {
    let timeoutId;
    return function debounced(...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function getSections(root) {
    if (!root || typeof root.querySelectorAll !== "function") return [];

    const sections = [];
    if (typeof root.matches === "function" && root.matches(SECTION_SELECTOR)) {
      sections.push(root);
    }

    sections.push(...root.querySelectorAll(SECTION_SELECTOR));
    return sections;
  }

  class TestimonialsSection {
    constructor(section) {
      this.section = section;
      this.viewport = section.querySelector(".ts2__viewport");
      this.track = section.querySelector(".ts2__track");
      this.items = this.track ? Array.from(this.track.children) : [];
      this.prev = section.querySelector(".ts2__arrow--prev");
      this.next = section.querySelector(".ts2__arrow--next");
      this.status = section.querySelector('[id^="ts2-status-"]');

      this.index = 0;
      this.perView = 1;
      this.maxIndex = 0;
      this.enabled = false;
      const parsedAutoscrollInterval = Number.parseInt(
        section.dataset.autoscrollInterval || "",
        10
      );

      this.autoscrollEnabled = section.dataset.autoscrollEnabled === "true";
      this.autoscrollInterval = Number.isFinite(parsedAutoscrollInterval)
        ? Math.max(2000, parsedAutoscrollInterval)
        : 4000;
      this.autoscrollTimer = null;
      this.autoscrollPauseReasons = new Set();
      this.prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      );

      this.dragging = false;
      this.dragStartX = 0;
      this.dragDeltaX = 0;

      this.onResize = debounce(this.refresh.bind(this), 120);
      this.onPrevClick = this.handlePrevClick.bind(this);
      this.onNextClick = this.handleNextClick.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onPointerDown = this.handlePointerDown.bind(this);
      this.onPointerMove = this.handlePointerMove.bind(this);
      this.onPointerUp = this.handlePointerUp.bind(this);
      this.onPointerCancel = this.handlePointerCancel.bind(this);
      this.onMouseEnter = this.handleMouseEnter.bind(this);
      this.onMouseLeave = this.handleMouseLeave.bind(this);
      this.onFocusIn = this.handleFocusIn.bind(this);
      this.onFocusOut = this.handleFocusOut.bind(this);
      this.onVisibilityChange = this.handleVisibilityChange.bind(this);
      this.onReducedMotionChange = this.handleReducedMotionChange.bind(this);
    }

    init() {
      if (!this.viewport || !this.track || this.items.length === 0) return false;

      if (this.prev) this.prev.addEventListener("click", this.onPrevClick);
      if (this.next) this.next.addEventListener("click", this.onNextClick);

      this.viewport.addEventListener("keydown", this.onKeydown);
      this.viewport.addEventListener("pointerdown", this.onPointerDown);
      this.viewport.addEventListener("pointermove", this.onPointerMove);
      this.viewport.addEventListener("pointerup", this.onPointerUp);
      this.viewport.addEventListener("pointercancel", this.onPointerCancel);
      this.viewport.addEventListener("pointerleave", this.onPointerCancel);
      this.section.addEventListener("mouseenter", this.onMouseEnter);
      this.section.addEventListener("mouseleave", this.onMouseLeave);
      this.section.addEventListener("focusin", this.onFocusIn);
      this.section.addEventListener("focusout", this.onFocusOut);

      window.addEventListener("resize", this.onResize);
      document.addEventListener("visibilitychange", this.onVisibilityChange);

      if (typeof this.prefersReducedMotion.addEventListener === "function") {
        this.prefersReducedMotion.addEventListener(
          "change",
          this.onReducedMotionChange
        );
      } else if (typeof this.prefersReducedMotion.addListener === "function") {
        this.prefersReducedMotion.addListener(this.onReducedMotionChange);
      }

      this.refresh();
      return true;
    }

    destroy() {
      if (this.prev) this.prev.removeEventListener("click", this.onPrevClick);
      if (this.next) this.next.removeEventListener("click", this.onNextClick);

      this.viewport.removeEventListener("keydown", this.onKeydown);
      this.viewport.removeEventListener("pointerdown", this.onPointerDown);
      this.viewport.removeEventListener("pointermove", this.onPointerMove);
      this.viewport.removeEventListener("pointerup", this.onPointerUp);
      this.viewport.removeEventListener("pointercancel", this.onPointerCancel);
      this.viewport.removeEventListener("pointerleave", this.onPointerCancel);
      this.section.removeEventListener("mouseenter", this.onMouseEnter);
      this.section.removeEventListener("mouseleave", this.onMouseLeave);
      this.section.removeEventListener("focusin", this.onFocusIn);
      this.section.removeEventListener("focusout", this.onFocusOut);

      window.removeEventListener("resize", this.onResize);
      document.removeEventListener("visibilitychange", this.onVisibilityChange);

      if (typeof this.prefersReducedMotion.removeEventListener === "function") {
        this.prefersReducedMotion.removeEventListener(
          "change",
          this.onReducedMotionChange
        );
      } else if (
        typeof this.prefersReducedMotion.removeListener === "function"
      ) {
        this.prefersReducedMotion.removeListener(this.onReducedMotionChange);
      }

      this.stopAutoscroll();
    }

    getPerView() {
      const cssValue = parseInt(
        window.getComputedStyle(this.section).getPropertyValue("--ts2-per-view"),
        10
      );

      if (Number.isFinite(cssValue) && cssValue > 0) return cssValue;

      if (window.innerWidth <= 749) return 1;
      if (window.innerWidth <= 1200) return 2;
      return 3;
    }

    getStepSize() {
      const firstItem = this.items[0];
      if (!firstItem) return 0;

      const trackStyle = window.getComputedStyle(this.track);
      const gap = parseFloat(trackStyle.columnGap || trackStyle.gap || "0");
      return firstItem.getBoundingClientRect().width + gap;
    }

    shouldEnable() {
      if (!this.prev || !this.next) return false;
      const hasOverflow = this.track.scrollWidth - this.viewport.clientWidth > 2;
      return this.items.length > this.perView && hasOverflow;
    }

    canAutoscroll() {
      return (
        this.autoscrollEnabled &&
        this.enabled &&
        this.maxIndex > 0 &&
        this.autoscrollPauseReasons.size === 0 &&
        !document.hidden &&
        !this.prefersReducedMotion.matches
      );
    }

    stopAutoscroll() {
      if (!this.autoscrollTimer) return;

      window.clearInterval(this.autoscrollTimer);
      this.autoscrollTimer = null;
    }

    startAutoscroll() {
      this.stopAutoscroll();
      if (!this.canAutoscroll()) return;

      this.autoscrollTimer = window.setInterval(() => {
        if (!this.canAutoscroll()) {
          this.stopAutoscroll();
          return;
        }

        const nextIndex = this.index >= this.maxIndex ? 0 : this.index + 1;
        this.goTo(nextIndex);
      }, this.autoscrollInterval);
    }

    syncAutoscroll() {
      if (this.canAutoscroll()) {
        this.startAutoscroll();
      } else {
        this.stopAutoscroll();
      }
    }

    setAutoscrollPaused(reason, paused) {
      if (!this.autoscrollEnabled) return;

      if (paused) {
        this.autoscrollPauseReasons.add(reason);
      } else {
        this.autoscrollPauseReasons.delete(reason);
      }

      this.syncAutoscroll();
    }

    refresh() {
      this.perView = this.getPerView();
      this.maxIndex = Math.max(0, this.items.length - this.perView);
      this.enabled = this.shouldEnable();

      if (!this.enabled) {
        this.index = 0;
      } else if (this.index > this.maxIndex) {
        this.index = this.maxIndex;
      }

      this.viewport.tabIndex = this.enabled ? 0 : -1;

      this.updatePosition(false);
      this.updateButtons();
      this.announce();
      this.syncAutoscroll();
    }

    updatePosition(animate) {
      const step = this.getStepSize();
      if (!this.enabled || !step) {
        this.track.style.transition = "none";
        this.track.style.transform = "translate3d(0,0,0)";
        return;
      }

      this.track.style.transition =
        animate && !this.prefersReducedMotion.matches
          ? "transform .58s cubic-bezier(.22,.61,.36,1)"
          : "none";
      this.track.style.transform = `translate3d(-${this.index * step}px,0,0)`;
    }

    updateButtons() {
      const show = this.enabled;
      [this.prev, this.next].forEach((button) => {
        if (!button) return;

        button.hidden = !show;
        button.disabled = !show;
        button.tabIndex = show ? 0 : -1;
        button.setAttribute("aria-hidden", String(!show));
        button.setAttribute("aria-disabled", String(!show));
      });

      if (!show || !this.prev || !this.next) return;

      const atStart = this.index <= 0;
      const atEnd = this.index >= this.maxIndex;

      this.prev.disabled = atStart;
      this.next.disabled = atEnd;
      this.prev.setAttribute("aria-disabled", String(atStart));
      this.next.setAttribute("aria-disabled", String(atEnd));
    }

    announce() {
      if (!this.status) return;

      if (!this.enabled) {
        this.status.textContent = `Showing all ${this.items.length} testimonials.`;
        return;
      }

      const first = this.index + 1;
      const last = Math.min(this.index + this.perView, this.items.length);
      this.status.textContent = `Showing testimonials ${first} to ${last} of ${this.items.length}.`;
    }

    goTo(nextIndex) {
      if (!this.enabled) return;

      const bounded = Math.max(0, Math.min(this.maxIndex, nextIndex));
      if (bounded === this.index) return;

      this.index = bounded;
      this.updatePosition(true);
      this.updateButtons();
      this.announce();
    }

    handlePrevClick() {
      this.goTo(this.index - 1);
      this.syncAutoscroll();
    }

    handleNextClick() {
      this.goTo(this.index + 1);
      this.syncAutoscroll();
    }

    handleKeydown(event) {
      if (!this.enabled) return;

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.goTo(this.index - 1);
        this.syncAutoscroll();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        this.goTo(this.index + 1);
        this.syncAutoscroll();
      }
    }

    handlePointerDown(event) {
      if (!this.enabled) return;

      this.setAutoscrollPaused("drag", true);
      this.dragging = true;
      this.dragStartX = event.clientX;
      this.dragDeltaX = 0;

      if (typeof this.viewport.setPointerCapture === "function") {
        this.viewport.setPointerCapture(event.pointerId);
      }
    }

    handlePointerMove(event) {
      if (!this.enabled || !this.dragging) return;
      this.dragDeltaX = event.clientX - this.dragStartX;
    }

    handlePointerUp(event) {
      if (!this.enabled || !this.dragging) return;

      this.dragging = false;
      if (
        typeof this.viewport.releasePointerCapture === "function" &&
        typeof this.viewport.hasPointerCapture === "function" &&
        this.viewport.hasPointerCapture(event.pointerId)
      ) {
        this.viewport.releasePointerCapture(event.pointerId);
      }

      const threshold = Math.max(44, this.viewport.clientWidth * 0.08);
      if (Math.abs(this.dragDeltaX) >= threshold) {
        this.goTo(this.dragDeltaX < 0 ? this.index + 1 : this.index - 1);
      } else {
        this.updatePosition(true);
      }

      this.dragDeltaX = 0;
      this.setAutoscrollPaused("drag", false);
    }

    handlePointerCancel() {
      const wasDragging = this.dragging;
      this.dragging = false;
      this.dragDeltaX = 0;
      this.updatePosition(true);
      if (wasDragging) {
        this.setAutoscrollPaused("drag", false);
      }
    }

    handleMouseEnter() {
      this.setAutoscrollPaused("hover", true);
    }

    handleMouseLeave() {
      this.setAutoscrollPaused("hover", false);
    }

    handleFocusIn() {
      this.setAutoscrollPaused("focus", true);
    }

    handleFocusOut(event) {
      if (event.relatedTarget && this.section.contains(event.relatedTarget)) {
        return;
      }
      this.setAutoscrollPaused("focus", false);
    }

    handleVisibilityChange() {
      this.syncAutoscroll();
    }

    handleReducedMotionChange() {
      this.syncAutoscroll();
    }

    selectBlock(target) {
      const card = target.closest(".ts2__item");
      if (!card) return;

      const selectedIndex = this.items.indexOf(card);
      if (selectedIndex === -1) return;

      this.goTo(Math.min(this.maxIndex, selectedIndex));
      this.syncAutoscroll();
    }
  }

  function init(root) {
    getSections(root).forEach((section) => {
      if (instances.has(section)) return;

      const instance = new TestimonialsSection(section);
      if (!instance.init()) return;

      instances.set(section, instance);
    });
  }

  function destroy(root) {
    getSections(root).forEach((section) => {
      const instance = instances.get(section);
      if (!instance) return;

      instance.destroy();
      instances.delete(section);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init(document));
  } else {
    init(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    destroy(event.target);
  });

  document.addEventListener("shopify:block:select", (event) => {
    const section = event.target.closest(SECTION_SELECTOR);
    if (!section) return;

    const instance = instances.get(section);
    if (!instance) return;

    instance.selectBlock(event.target);
  });
})();
